import { grey, red } from '@mui/material/colors'
import { createTheme, ThemeOptions } from '@mui/material/styles'

import common from './colors/common'
import primary from './colors/primary'
import secondary from './colors/secondary'
import support from './colors/support'
import textExtra from './colors/text'
import ui from './colors/ui'
import MuiButtonStyles from './styles/MuiButton.styles'

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    grey: true
  }
}

declare module '@mui/material' {
  interface Color {
    main: string
    dark: string
  }
}

const palette: ThemeOptions['palette'] = {
  common: { black: common.black, white: common.white },
  grey: {
    main: grey[300],
    dark: grey[400],
  },
  background: { paper: common.white, default: ui.two },
  moduleHeader: {
    backgroundColor: common.white,
  },
  widget: {
    header: {
      backgroundColor: ui.seven,
      color: common.black,
    },
  },
  menus: {
    sidebar: {
      listItemActive: primary.main,
      listItemHover: primary.hover,
      main: ui.five,
      nestedListItem: primary.hover,
      text: ui.one,
      nestedListItemActive: ui.six,
    },
    topbar: {
      main: ui.five,
      text: ui.one,
    },
  },
  ui: ui,
  primary: primary,
  secondary: secondary,
  textExtra: textExtra,
  support: support,
  error: red,
  contrastThreshold: 3,
  tonalOffset: 0.2,
}

// Honor prefers-reduced-motion: when the OS / browser context requests reduced motion
// (Playwright + Robot set this in e2e), zero MUI's JS-driven transition durations so
// dialogs/menus/poppers appear instantly. This is legit a11y support and removes the
// transition-driven flakiness that forced `force=True` clicks in the e2e suite.
const prefersReducedMotion =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

export const themeProps: ThemeOptions = {
  ...(prefersReducedMotion
    ? {
        transitions: {
          create: () => 'none',
          duration: {
            shortest: 0,
            shorter: 0,
            short: 0,
            standard: 0,
            complex: 0,
            enteringScreen: 0,
            leavingScreen: 0,
          },
        },
      }
    : {}),
  mixins: {
    drawer: {
      width: 240,
    },
    toolbar: {
      minHeight: 48,
    },
    widget: {
      header: {
        height: 40,
      },
    },
  },
  palette,
  typography: {
    fontFamily: `"Open Sans", "Helvetica", "Arial", sans-serif`,
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    h1: {
      fontFamily: `"Raleway", "Open Sans", "Helvetica", "Arial", sans-serif`,
      fontSize: 24,
      fontWeight: 800,
    },
    h2: {
      fontFamily: `"Raleway", "Open Sans", "Helvetica", "Arial", sans-serif`,
      fontSize: 16,
      fontWeight: 500,
    },
    h3: {
      fontFamily: `"Raleway", "Open Sans", "Helvetica", "Arial", sans-serif`,
      fontSize: 14,
      fontWeight: 500,
    },
    h4: {
      fontFamily: `"Raleway", "Open Sans", "Helvetica", "Arial", sans-serif`,
      fontSize: 14,
      fontWeight: 500,
    },
    h5: {
      fontFamily: `"Raleway", "Open Sans", "Helvetica", "Arial", sans-serif`,
      fontSize: 16,
      fontWeight: 500,
    },
    h6: {
      fontFamily: `"Raleway", "Open Sans", "Helvetica", "Arial", sans-serif`,
      fontSize: 16,
      fontWeight: 500,
    },

    button: {
      textTransform: 'none',
    },
    body1: { fontSize: 14 },
  },
  components: {
    // Disable non-MUI CSS transitions/animations under reduced motion (covers e.g. the
    // EventsCalendar custom date-picker `transition: transform 0.3s`). Pairs with the
    // zeroed MUI durations above.
    MuiCssBaseline: {
      styleOverrides: {
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            transitionDuration: '0.01ms !important',
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: MuiButtonStyles,
    },
    MuiIcon: {
      styleOverrides: {
        root: {
          overflow: `visible`,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: `none`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: `10px`,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          height: 10,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          height: 10,
          minHeight: 0,
        },
      },
    },
  },
}

const theme = createTheme(themeProps)

export default theme
